export type SupportedLanguage = "en" | "zh-Hant" | "zh-Hans";

export type ServiceWorkerRegistrationLike = {
  update(): Promise<unknown>;
  waiting?: { postMessage(message: unknown): void } | null;
};

export type ServiceWorkerContainerLike = {
  getRegistration(scope?: string): Promise<ServiceWorkerRegistrationLike | undefined>;
};

export const PWA_UPDATE_EVENT = "posnext:pwa-update-available";
let updateReloadArmed = false;

export function languageTag(language: SupportedLanguage): string {
  return language === "zh-Hant" ? "zh-Hant" : language === "zh-Hans" ? "zh-Hans" : "en";
}

export function registerPwa(serviceWorkerPath: string): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  void navigator.serviceWorker.register(serviceWorkerPath, { scope: "/" }).then((registration) => {
    const announceUpdate = (): void => {
      if (!navigator.serviceWorker.controller) return;
      window.dispatchEvent(new Event(PWA_UPDATE_EVENT));
    };
    const watchInstallingWorker = (): void => {
      const worker = registration.installing;
      if (!worker) return;
      const onStateChange = (): void => {
        if (worker.state === "installed") announceUpdate();
      };
      if (worker.state === "installed") onStateChange();
      else worker.addEventListener("statechange", onStateChange, { once: true });
    };
    registration.addEventListener("updatefound", watchInstallingWorker);
    if (registration.waiting && navigator.serviceWorker.controller) announceUpdate();
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!updateReloadArmed) return;
      updateReloadArmed = false;
      window.location.reload();
    });
  }).catch(() => undefined);
}

const PRELOAD_RELOAD_KEY = "posnext.preloadReloadAt";
const PRELOAD_RELOAD_WINDOW_MS = 15_000;

/**
 * Recover a tab that still holds an old Vite chunk after a static deploy.
 *
 * A single guarded reload is safe for a normal asset rollover. The short
 * sessionStorage window prevents a broken deployment from creating a reload
 * loop, while leaving the original error visible for diagnosis.
 */
export function installPreloadRecovery(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    let previous = 0;
    try {
      previous = Number(window.sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0);
    } catch {
      // sessionStorage can be blocked; the normal browser error still remains.
    }
    if (Date.now() - previous <= PRELOAD_RELOAD_WINDOW_MS) return;
    try {
      window.sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
    } catch {
      // A storage failure must not prevent the guarded reload attempt.
    }
    event.preventDefault();
    window.location.reload();
  });
}

export async function requestPwaUpdate(container: ServiceWorkerContainerLike | undefined = typeof navigator === "undefined" ? undefined : navigator.serviceWorker): Promise<"updated" | "checked" | "unavailable"> {
  if (!container) return "unavailable";
  const registration = await container.getRegistration("/");
  if (!registration) return "unavailable";
  await registration.update();
  if (registration.waiting) {
    updateReloadArmed = true;
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
    return "updated";
  }
  return "checked";
}
